import * as globals from '@wandb/weave/common/css/globals.styles';
import {Button} from '@wandb/weave/components/Button';
import {IconSearch} from '@wandb/weave/components/Icon';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {Analytics} from '../services/analytics';

const HEIGHT_TRANSITION_DURATION_MS = 100;
const TEXTAREA_MIN_ROWS_HEIGHT = 34;

const SearchInputContainer = styled.div`
  display: flex;
`;
SearchInputContainer.displayName = 'S.SearchInputContainer';

const SearchInput = styled(TextareaAutosize)<{
  $shouldTransitionHeight: boolean;
}>`
  width: 100%;
  min-width: 200px;
  resize: none;
  line-height: 24px;
  padding: 4px 32px;
  border-radius: 4px;
  border: 1px solid ${globals.MOON_250};
  transition: ${p =>
    p.$shouldTransitionHeight
      ? `height ${HEIGHT_TRANSITION_DURATION_MS}ms`
      : 'none'};
  word-break: break-all;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
  &::placeholder {
    color: ${globals.MOON_500};
  }
  &:focus,
  &:focus-visible {
    border: 1px solid ${globals.TEAL_400};
    box-shadow: 0 0 0 1px ${globals.TEAL_400};
    outline: none;
  }
`;
SearchInput.displayName = 'S.SearchInput';

const SearchInputIcon = styled(IconSearch)<{
  $hasValue: boolean;
}>`
  height: 18px;
  width: 18px;
  color: ${props => (props.$hasValue ? globals.MOON_800 : globals.MOON_500)};
  position: absolute;
  top: 8px;
  left: 8px;
`;
SearchInputIcon.displayName = 'S.SearchInputIcon';

interface RunsSearchBarProps {
  isGroupSearch: boolean;
  isInReport?: boolean;
  search: {
    query: string;
    isRegex: boolean;
  };
  setSearch(search: string, isRegex: boolean): void;
}

const RunsSearchBarComp: React.FC<RunsSearchBarProps> = ({
  isGroupSearch,
  isInReport,
  search: propsSearch,
  setSearch,
}) => {
  const [inputValue, setInputValue] = useState(propsSearch.query);
  const [regex, setRegex] = useState(propsSearch.isRegex);
  const [maxRows, setMaxRows] = useState(1);
  const [isCollapsible, setIsCollapsible] = useState(false);
  const [shouldTransitionHeight, setShouldTransitionHeight] = useState(false);

  /**
   * Used for analytics tracking. We want to log "Runs searched" event
   * on blur, but only if the value has actually changed since focused.
   */
  const [valueAtFocus, setValueAtFocus] = useState('');

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dontBlurRef = useRef(false);

  const debouncedSearch = useMemo(
    () =>
      _.debounce((value: string) => {
        if (value !== propsSearch.query || regex !== propsSearch.isRegex) {
          setSearch(value, regex);
        }
      }, 500),
    [propsSearch, regex, setSearch]
  );

  const handleInputValueChange = useCallback(
    (e: {target: {value: string}}) => {
      setInputValue(e.target.value);
    },
    [setInputValue]
  );

  const toggleRegex = useCallback(() => {
    // HAX: prevent the search bar from blurring
    dontBlurRef.current = true;
    setTimeout(() => {
      dontBlurRef.current = false;
      inputRef.current?.focus();
    });

    setRegex(prev => {
      const next = !prev;
      Analytics.track('Runs search regex toggled', {on: next});
      return next;
    });
  }, []);

  /**
   * Height transition should only apply on expand/collapse,
   * and must be cleared once the animation is completed.
   * Otherwise, the user will experience weird scroll/jumping
   * behavior as they're typing and enter onto a new line.
   */
  const applyHeightTransition = useCallback(() => {
    setShouldTransitionHeight(true);
    setTimeout(
      () => setShouldTransitionHeight(false),
      HEIGHT_TRANSITION_DURATION_MS
    );
  }, []);

  useEffect(() => {
    const input = inputValue.trim();
    debouncedSearch(input);
  }, [inputValue, debouncedSearch, propsSearch, regex]);

  return (
    <div className="runs-search-bar">
      <SearchInputContainer>
        <SearchInputIcon $hasValue={inputValue !== ''} />
        <SearchInput
          ref={inputRef}
          minRows={1}
          maxRows={maxRows}
          value={inputValue}
          placeholder={isGroupSearch ? 'Search groups' : 'Search runs'}
          spellCheck="false"
          onChange={handleInputValueChange}
          onFocus={() => {
            setMaxRows(16);
            applyHeightTransition();
            if (valueAtFocus !== inputValue) {
              setValueAtFocus(inputValue);
            }
          }}
          onBlur={() => {
            if (dontBlurRef.current) {
              return;
            }
            setInputValue(q => q.trim());
            if (inputValue !== valueAtFocus) {
              Analytics.track('Runs searched', {
                location: isInReport ? 'report' : 'runs table',
                regex: regex ? 'on' : 'off',
              });
              setValueAtFocus('');
            }
          }}
          onHeightChange={height => {
            setIsCollapsible(height > TEXTAREA_MIN_ROWS_HEIGHT);
          }}
          $shouldTransitionHeight={shouldTransitionHeight}
        />
        {isCollapsible && (
          <Button
            size="small"
            variant="quiet"
            icon="expand-right"
            className="absolute bottom-5 right-6 -rotate-90"
            twWrapperStyles={{display: 'block'}}
            onClick={() => {
              setMaxRows(1);
              applyHeightTransition();
              inputRef.current?.scroll({top: 0});
              Analytics.track('runs-search-collapsed', {
                inputLength: inputValue.length,
                inputHeight: inputRef.current?.offsetHeight,
              });
            }}
          />
        )}
      </SearchInputContainer>
      <Popup
        className="runs-search-bar-regex-toggle-popup"
        content={`Regex matching is ${regex ? 'on' : 'off'}`}
        position="top center"
        popperModifiers={{
          preventOverflow: {
            boundariesElement: 'viewport',
          },
        }}
        inverted
        trigger={
          <Button
            aria-label="Toggle regex matching"
            size="small"
            variant="ghost"
            icon="regex"
            twWrapperStyles={{display: 'block'}}
            className={classNames(
              'absolute right-6 top-5 bg-white dark:bg-moon-950',
              {
                // custom hex values are because this button needs to have opaque
                // bg color, but the base component uses colors with opacity
                //   - #f5f5f5 = oblivion/5%
                //   - #d7f6f9 = teal-300/48%
                //   - #163942 = teal-700/40%
                'text-moon-500 hover:bg-[#f5f5f5] hover:text-moon-800': !regex,
                'dark:hover:bg-moon-750 dark:hover:text-moon-100': !regex,
                'bg-[#d7f6f9] dark:bg-[#163942]': regex,
              }
            )}
            onMouseDown={toggleRegex}
            active={regex}
          />
        }
      />
    </div>
  );
};

export const RunsSearchBar = memo(RunsSearchBarComp);
